import { FC, useState, useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import {
  selectActiveBankAccount,
  selectActiveCompany,
} from '@features/workspace/store/selectors';
import { toast } from 'react-toastify';
import Toast from '@components/ui/Toast';
import { connectApi } from '@app/api';
import moment from 'moment';
import FileDownload from 'js-file-download';
import { IStatement } from '@features/workspace/types/banking';
import NoStatements from './components/NoStatements';
import StatementsTable from './components/StatementsTable';
import logger from '@/app/services/logger';

const BankingAccountStatements: FC = () => {
  const activeBankAccount = useAppSelector(selectActiveBankAccount);
  const company = useAppSelector(selectActiveCompany);

  const [selectedStatements, setSelectedStatements] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);

  const statements = useMemo<IStatement[]>(() => {
    if (!activeBankAccount?.createdAt) {
      return [];
    }

    const generatedStatements: IStatement[] = [];
    const startDate = moment(activeBankAccount.createdAt).startOf('month');
    const endDate = moment().startOf('month');

    if (!startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    const current = endDate.clone();

    while (current.isSameOrAfter(startDate)) {
      generatedStatements.push({
        id: `stmt_${current.format('YYYYMM')}`,
        date: current.toISOString(),
        filename: `${current.format('MMMM YYYY')}.pdf`,
      });
      current.subtract(1, 'month');
    }

    return generatedStatements;
  }, [activeBankAccount]);

  const handleSelectStatement = (id: string) => {
    setSelectedStatements((prev) =>
      prev.includes(id) ? prev.filter((stmtId) => stmtId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedStatements.length === statements.length) {
      setSelectedStatements([]);
    } else {
      setSelectedStatements(statements.map((s) => s.id));
    }
  };

  const handleDownload = async () => {
    if (!company?.id || !activeBankAccount?.bankAccountNumber) return;

    setIsDownloading(true);
    try {
      const selectedStatementObjects =
        statements.filter((stmt) => selectedStatements.includes(stmt.id)) || [];

      // eslint-disable-next-line no-restricted-syntax
      for (const statement of selectedStatementObjects) {
        const dateFrom = moment(statement.date).startOf('month').toISOString();
        let dateToMoment = moment(statement.date).endOf('month');
        const now = moment().subtract(2, 'minutes');

        if (dateToMoment.isAfter(now)) {
          dateToMoment = now;
        }

        const dateTo = dateToMoment.toISOString();

        // eslint-disable-next-line no-await-in-loop
        const response = await connectApi.transaction.downloadStatement(
          company.id,
          {
            bankAccountNumber: activeBankAccount.bankAccountNumber,
            transactionDateFrom: dateFrom,
            transactionDateTo: dateTo,
          }
        );

        FileDownload(response.data, statement.filename);
      }

      toast(
        <Toast
          showCloseButton
          title="Statements successfully downloaded"
          variant="success"
        />,
        {
          hideProgressBar: true,
          autoClose: 5000,
          closeButton: false,
        }
      );
      setSelectedStatements([]);
    } catch (error) {
      logger.error('Failed to download statements', error);
      toast(
        <Toast
          showCloseButton
          text="Something went wrong while downloading the statements. Please refresh and try again."
          title="Oh no, something went wrong"
          variant="critical"
        />,
        {
          hideProgressBar: true,
          autoClose: 5000,
          closeButton: false,
        }
      );
    } finally {
      setIsDownloading(false);
    }
  };

  if (!statements || statements.length === 0) {
    return <NoStatements />;
  }

  return (
    <StatementsTable
      isSending={isDownloading}
      selectedStatements={selectedStatements}
      statements={statements}
      onDownload={handleDownload}
      onSelectAll={handleSelectAll}
      onSelectStatement={handleSelectStatement}
    />
  );
};

export default BankingAccountStatements;
